/**
 * NIR Apps Grid — modal, bookmark, clipboard utilities.
 * Migrated from nir-apps.js (421 lines).
 */

// ── Notification styles (injected once) ──────────────────────────

function ensureNotificationStyles(): void {
  if (document.querySelector("#nir-notification-styles")) return;
  const style = document.createElement("style");
  style.id = "nir-notification-styles";
  style.textContent = `
    @keyframes slideIn { from { transform: translateX(-50%) translateY(-100%); opacity: 0; } to { transform: translateX(-50%) translateY(0); opacity: 1; } }
    @keyframes slideOut { from { transform: translateX(-50%) translateY(0); opacity: 1; } to { transform: translateX(-50%) translateY(-100%); opacity: 0; } }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes fadeOut { from { opacity: 1; } to { opacity: 0; } }
    @keyframes scaleIn { from { transform: scale(0.9); opacity: 0; } to { transform: scale(1); opacity: 1; } }
    .nir-bookmark-url:hover { background: #e2e8f0 !important; }
    .nir-copy-link-btn:hover { transform: translateY(-2px); }
    .nir-close-modal-btn:hover { background: #cbd5e1 !important; }
  `;
  document.head.appendChild(style);
}

// ── Notification ─────────────────────────────────────────────────

function showNotification(message: string): void {
  document.querySelector(".nir-notification")?.remove();
  ensureNotificationStyles();

  const notification = document.createElement("div");
  notification.className = "nir-notification";
  notification.textContent = message;
  notification.style.cssText =
    "position:fixed;top:20px;left:50%;transform:translateX(-50%);background:#4CAF50;" +
    "color:white;padding:15px 25px;border-radius:5px;box-shadow:0 2px 10px rgba(0,0,0,0.2);" +
    "z-index:1000001;font-size:14px;max-width:80%;text-align:center;animation:slideIn 0.3s ease-out;";
  document.body.appendChild(notification);

  setTimeout(() => {
    notification.style.animation = "slideOut 0.3s ease-in";
    notification.style.animationFillMode = "forwards";
    setTimeout(() => notification.remove(), 300);
  }, 4000);
}

// ── Clipboard ────────────────────────────────────────────────────

function fallbackCopyToClipboard(text: string): void {
  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.style.cssText =
    "position:fixed;top:0;left:0;width:2em;height:2em;padding:0;border:none;outline:none;box-shadow:none;background:transparent;";
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  try {
    if (document.execCommand("copy")) {
      showNotification("URL скопійовано в буфер обміну!");
    } else {
      showNotification("Не вдалося скопіювати URL. Скопіюйте вручну: " + text);
    }
  } catch {
    showNotification("Не вдалося скопіювати URL. Скопіюйте вручну: " + text);
  }
  document.body.removeChild(textArea);
}

function copyToClipboard(text: string): void {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(
      () => showNotification("URL скопійовано в буфер обміну!"),
      () => fallbackCopyToClipboard(text),
    );
  } else {
    fallbackCopyToClipboard(text);
  }
}

// ── Focus trap ───────────────────────────────────────────────────

function trapFocus(element: HTMLElement): void {
  const focusable = element.querySelectorAll<HTMLElement>(
    'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select',
  );
  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  first?.focus();

  element.addEventListener("keydown", (e) => {
    if (e.key === "Tab") {
      if (e.shiftKey) {
        if (document.activeElement === first) {
          last?.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === last) {
          first?.focus();
          e.preventDefault();
        }
      }
    }
    if (e.key === "Escape") closeNIRAppsGrid();
  });
}

// ── Apps grid modal ──────────────────────────────────────────────

export function openNIRAppsGrid(): void {
  const modal = document.getElementById("nir-apps-modal-overlay");
  if (modal) {
    modal.classList.add("active");
    document.body.style.overflow = "hidden";
    trapFocus(modal);
  }
}

export function closeNIRAppsGrid(): void {
  const modal = document.getElementById("nir-apps-modal-overlay");
  if (modal) {
    modal.classList.remove("active");
    document.body.style.overflow = "";
  }
}

// ── Bookmark modal ───────────────────────────────────────────────

function closeBookmarkModal(): void {
  const modal = document.querySelector<HTMLElement>(".nir-bookmark-modal-overlay");
  if (modal) {
    modal.style.animation = "fadeOut 0.3s ease";
    setTimeout(() => modal.remove(), 300);
  }
}

function showBookmarkModal(url: string, title: string): void {
  document.querySelector(".nir-bookmark-modal-overlay")?.remove();
  ensureNotificationStyles();

  const overlay = document.createElement("div");
  overlay.className = "nir-bookmark-modal-overlay";
  overlay.style.cssText =
    "position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.85);" +
    "backdrop-filter:blur(15px);z-index:1000000;display:flex;align-items:center;" +
    "justify-content:center;animation:fadeIn 0.3s ease;";

  overlay.innerHTML = `
    <div style="background:linear-gradient(145deg,#ffffff,#f8fafc);border-radius:20px;padding:35px;max-width:550px;width:90%;box-shadow:0 25px 80px rgba(0,0,0,0.4);text-align:center;font-family:'Segoe UI',Tahoma,Geneva,Verdana,sans-serif;border:1px solid rgba(255,255,255,0.3);animation:scaleIn 0.3s ease;">
      <h2 style="background:linear-gradient(135deg,#1e3c72,#2a5298);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;margin-bottom:15px;font-size:26px;font-weight:800;">Додати Портал до закладок</h2>
      <div style="background:linear-gradient(135deg,#dcfce7,#bbf7d0);border:2px solid #16a34a;border-radius:12px;padding:20px;margin:20px 0;">
        <div style="color:#15803d;font-weight:700;margin-bottom:8px;font-size:16px;">Назва закладки:</div>
        <div style="color:#166534;font-size:14px;line-height:1.4;">${title}</div>
      </div>
      <p style="color:#64748b;margin-bottom:20px;line-height:1.6;font-size:15px;"><strong>Для автоматичного додавання:</strong><br>Натисніть <kbd style="background:#e2e8f0;padding:3px 8px;border-radius:4px;font-weight:bold;">Ctrl+D</kbd> (або <kbd style="background:#e2e8f0;padding:3px 8px;border-radius:4px;font-weight:bold;">Cmd+D</kbd> на Mac)</p>
      <div class="nir-bookmark-url" style="background:#f1f5f9;border:2px solid #cbd5e1;border-radius:12px;padding:15px;margin:15px 0;font-family:monospace;font-size:13px;word-break:break-all;cursor:pointer;transition:0.3s;">${url}</div>
      <div style="display:flex;gap:12px;justify-content:center;margin-top:25px;flex-wrap:wrap;">
        <button class="nir-copy-link-btn" style="background:linear-gradient(135deg,#f59e0b,#d97706);color:white;border:none;padding:12px 20px;border-radius:10px;cursor:pointer;font-weight:600;font-size:14px;transition:all 0.3s ease;box-shadow:0 4px 15px rgba(245,158,11,0.3);">Копіювати посилання</button>
        <button class="nir-close-modal-btn" style="background:#e2e8f0;color:#64748b;border:none;padding:12px 20px;border-radius:10px;cursor:pointer;font-weight:600;font-size:14px;transition:all 0.3s ease;">Закрити</button>
      </div>
    </div>`;

  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) closeBookmarkModal();
  });
  document.body.appendChild(overlay);

  overlay.querySelector(".nir-bookmark-url")?.addEventListener("click", () => copyToClipboard(url));
  overlay.querySelector(".nir-copy-link-btn")?.addEventListener("click", () => copyToClipboard(url));
  overlay.querySelector(".nir-close-modal-btn")?.addEventListener("click", () => closeBookmarkModal());
}

export function addEmployeePortalBookmark(): void {
  const url = window.location.href.split("?")[0] + "?widget=auto&employee=1";
  const title = "Портал інформаційних ресурсів для співробітників - NIR.gov.ua";
  showBookmarkModal(url, title);
}

// ── Init + window exports ────────────────────────────────────────

document.addEventListener("DOMContentLoaded", () => {
  const modal = document.getElementById("nir-apps-modal-overlay");
  if (modal) {
    modal.addEventListener("click", (e) => {
      if (e.target === modal) closeNIRAppsGrid();
    });
  }

  document.querySelector(".nir-apps-close")?.addEventListener("click", closeNIRAppsGrid);

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && modal?.classList.contains("active")) {
      closeNIRAppsGrid();
    }
  });

  document.querySelectorAll<HTMLElement>(".nir-app-item").forEach((item) => {
    const bgColor = item.dataset.bgcolor ?? item.dataset.bgColor;
    if (bgColor) item.style.setProperty("--app-bg-color", bgColor);
  });

  document.querySelectorAll(".nir-apps-button").forEach((button) => {
    button.addEventListener("click", (e) => {
      e.preventDefault();
      openNIRAppsGrid();
    });
  });
});

// Window exports for onclick handlers in templates
window.openNIRAppsGrid = openNIRAppsGrid;
window.closeNIRAppsGrid = closeNIRAppsGrid;
window.addEmployeePortalBookmark = addEmployeePortalBookmark;
